import type { LocationMst } from '@libs/domain';
import { locationMst } from '@libs/domain';
import type { MstRangeSearchProps, MstSearchDialogProps, MstSearchProps } from '@libs/templates/mst';
import { MstSearchDialogTmpl } from '@libs/templates/mst';
import type { Characters, Item, PageProps, WithExclusion } from '@ps-smart-cp/ppfw-client';
import {
  MasterRangeSearchField,
  MasterSearch,
  MasterSearchField,
  withRerender,
} from '@ps-smart-cp/ppfw-client';
import { AdditionalInfo, locationMstColumns } from './components';
import { useLocationItems } from './hooks';
import { locationMstListSearchFieldConfig } from './item-config';
import type { LocationMstRegistration } from './models';
import { LocationMstListSearch } from './models';
import { fetchMst, listRequest } from './repositories';

function InnerComponent(props: MstSearchDialogProps<LocationMstRegistration> & PageProps) {
  const locationItems = useLocationItems();

  return (
    <MstSearchDialogTmpl
      mstName={locationItems ? locationItems.name : locationMst.names.location}
      columns={locationMstColumns}
      items={locationMstListSearchFieldConfig}
      listRequestFunction={listRequest}
      initMst={LocationMstListSearch.init}
      {...props}
    >
      {AdditionalInfo}
    </MstSearchDialogTmpl>
  );
}

const Dialog = withRerender(InnerComponent, 'LocationMstDialog');

function LocationMasterSearchField<
  T extends Pick<LocationMst['schema'], K>,
  K extends 'locationCode' | 'locationMstCode',
  I extends Record<K, Item<Characters>>,
>(props: MstSearchProps<T, K, I>) {
  return (
    <MasterSearchField getMaster={async (code) => fetchMst({ code })} {...props}>
      {(setIsOpen, setMaster, getMst) => (
        <Dialog setIsOpen={setIsOpen} setMaster={setMaster} getMaster={getMst} />
      )}
    </MasterSearchField>
  );
}

function LocationMasterSearch<
  T extends Pick<LocationMst['schema'], K>,
  K extends 'locationCode' | 'locationMstCode',
  I extends Record<K, Item<Characters>>,
>(props: MstSearchProps<T, K, I>) {
  return (
    <MasterSearch
      getMaster={async (code) => fetchMst({ code })}
      itemAttribute={props.items[props.prop].attribute}
      {...props}
    >
      {(setIsOpen, setMaster, getMst) => (
        <Dialog setIsOpen={setIsOpen} setMaster={setMaster} getMaster={getMst} />
      )}
    </MasterSearch>
  );
}

function LocationMasterRangeSearch<
  T extends Pick<LocationMst['schema'], K>,
  K extends 'locationCodeRange' | 'locationMstCodeRange',
  I extends Record<K, Item<WithExclusion>>,
>(props: MstRangeSearchProps<T, K, I>) {
  return (
    <MasterRangeSearchField getMaster={async (code) => fetchMst({ code })} {...props}>
      {(setIsOpen, setMaster, getMst) => (
        <Dialog setIsOpen={setIsOpen} setMaster={setMaster} getMaster={getMst} />
      )}
    </MasterRangeSearchField>
  );
}

export { LocationMasterRangeSearch, LocationMasterSearch, LocationMasterSearchField };
